import config from "@Config/index";
import {
  renderChinaCompositeDateRange,
  renderChinaNamedDate,
  renderChinaNamedMonth,
} from "@Utils/cnDisplayDate";
import {
  ANONYMOUS_REVIEWER_KEY,
  NAMELESS_REVIEWER_KEY,
  isAnonymousReview,
} from "./anonymousReviewer";
import { getRandomAvatar } from "./avatars";
import { ReviewData, ReviewPhoto, CategoryRating, PhotoItem } from "./types";
import { REVIEW_VIDEO_PLATFORM } from "./utils";

interface RawReview {
  id?: string;
  anonymous?: boolean;
  heading?: { heading: string } | string;
  rating?: number;
  review?: string;
  content?: string;
  room_type?: string;
  booking_dates?: string;
  booking_date?: string;
  helpful_count?: number;
  timestamp?: string;
  created_at?: string;
  media?: Array<{ path?: string; thumbnail_url?: string; platform?: string }>;
  user_details?: {
    name?: string;
    avatar?: string;
    university?: string;
    program?: string;
    room_type?: string;
  };
  booking_details?: {
    booking_id?: string;
    room_type?: string;
    room_type_id?: string;
    tenancy?: string;
    property_name?: string;
    nationality?: string;
    enrolment_status?: string;
    university?: string;
  };
}

export interface RawReviewsInput {
  reviewsRating?: string | number | null;
  reviewSummary?: {
    summary?: string;
    rating?: Record<string, string>;
  } | null;
  rawReviews?: RawReview[] | null;
  propertyCity?: string;
  propertyName?: string;
}

export interface ReviewsSectionBuiltData {
  overallRating: number;
  ratingLabel: string; // Tolgee key for rating label (e.g. "propertypage.reviews.ratinglabel.excellent")
  reviewCount: number;
  categories: CategoryRating[];
  summary: string;
  photos: PhotoItem[];
  reviews: ReviewData[];
  /** `reviews` minus empty ones (P1D-5558); the panel and gallery still use `reviews`. */
  carouselReviews: ReviewData[];
  mediaArray: any[];
  mediaIndexMap: Map<string, number>;
}

export function getRatingLabel(rating: number): string {
  if (rating >= 4.5) return "propertypage.reviews.ratinglabel.excellent";
  if (rating >= 4.0) return "propertypage.reviews.ratinglabel.verygood";
  if (rating >= 3.5) return "propertypage.reviews.ratinglabel.good";
  if (rating >= 3.0) return "propertypage.reviews.ratinglabel.average";
  return "propertypage.reviews.ratinglabel.belowaverage";
}

const CATEGORY_PRIORITY: Array<{ key: string; label: string; labelKey: string }> = [
  { key: "management", label: "Management", labelKey: "propertypage.reviews.category.management" },
  { key: "cleaning", label: "Cleanliness", labelKey: "propertypage.reviews.category.cleanliness" },
  {
    key: "value_for_money",
    label: "Best Value",
    labelKey: "propertypage.reviews.category.bestvalue",
  },
  { key: "amenities", label: "Amenities", labelKey: "propertypage.reviews.category.amenities" },
  { key: "location", label: "Location", labelKey: "propertypage.reviews.category.location" },
  { key: "community", label: "Community", labelKey: "propertypage.reviews.category.community" },
  { key: "staff", label: "Staff", labelKey: "propertypage.reviews.category.staff" },
  { key: "social", label: "Social", labelKey: "propertypage.reviews.category.social" },
  { key: "internet", label: "Internet", labelKey: "propertypage.reviews.category.internet" },
  { key: "food", label: "Food", labelKey: "propertypage.reviews.category.food" },
];

function buildCategories(rating?: Record<string, string> | null): CategoryRating[] {
  if (!rating || typeof rating !== "object") return [];
  const categories: CategoryRating[] = [];
  for (const { key, label, labelKey } of CATEGORY_PRIORITY) {
    const score = parseFloat(rating[key]);
    if (!Number.isNaN(score) && score > 0) {
      categories.push({ label, labelKey, score });
    }
  }
  return categories.sort((a, b) => b.score - a.score).slice(0, 6);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const legacy = `${months[date.getMonth()]}' ${String(date.getFullYear()).slice(2)}`;
  if (config.IS_CHINA_APP) {
    const fullNamed = renderChinaNamedDate(dateStr, "");
    if (fullNamed) return fullNamed;
    return renderChinaNamedMonth(dateStr, legacy) || legacy;
  }
  return legacy;
}

function formatReviewDateRange(raw?: string): string {
  if (!raw) return "";
  if (config.IS_CHINA_APP) {
    return renderChinaCompositeDateRange(raw, raw) || raw;
  }
  return raw;
}

const HEADINGS_BY_RATING: Record<number, string[]> = {
  1.0: [
    "Needs Urgent Improvement",
    "Disappointing Accommodation Experience",
    "Sub-Standard Operational Quality",
    "Poor Overall Student Experience",
    "Below Expectations in Every Aspect",
    "Unsatisfactory Housing Conditions",
    "Significant Service Quality Concerns",
    "Not Recommended for Most Students",
  ],

  2.0: [
    "Mixed Review Experience",
    "Average Stay Conditions",
    "Mediocre On-Site Services",
    "Room for Significant Improvement",
    "Inconsistent Accommodation Standards",
    "Below-Average Student Living Experience",
    "Acceptable but Lacking Key Features",
    "Several Areas Need Attention",
  ],

  3.0: [
    "Decent Choice for Students",
    "Comfortable, Standard Stay",
    "Satisfactory Accommodation Quality",
    "Balanced Living Experience",
    "Reasonably Reliable Student Housing",
    "Meets Basic Student Expectations",
    "Good Value with Some Limitations",
    "Generally Positive Accommodation Experience",
  ],

  4.0: [
    "Highly Commendable Option",
    "Enjoyable, Fluid Stay Experience",
    "Very Reliable Housing Setup",
    "Strongly Recommended by Residents",
    "Well-Maintained Student Accommodation",
    "Consistently Positive Living Environment",
    "Quality Services and Facilities",
    "Excellent Choice for Student Living",
  ],

  5.0: [
    "Outstanding Student Experience",
    "Exceptional Living Environment",
    "Perfectly Managed Housing Choice",
    "Top-Rated Accommodation Experience",
    "Best-in-Class Student Residence",
    "Exceptional Comfort and Convenience",
    "Highly Praised by Student Residents",
    "Premium Housing with Outstanding Service",
  ],
};

function getFallbackHeading(rating: number, index: number, prevHeading?: string): string {
  const roundedRating = Math.round(rating);
  const clampedRating = Math.min(5, Math.max(1, roundedRating)) as number;
  const headings = HEADINGS_BY_RATING[clampedRating] || HEADINGS_BY_RATING[3.0];

  let heading = headings[index % headings.length];

  if (heading === prevHeading) {
    heading = headings[(index + 1) % headings.length];
  }

  return heading;
}

/**
 * Check if a review title is a Tolgee translation key (fallback heading)
 * vs user-generated content from the API.
 */
export function isFallbackHeadingKey(title: string): boolean {
  return title.startsWith("propertypage.reviews.heading.");
}

/**
 * Abbreviate a full name to "FirstName L" format for privacy.
 * e.g. "Dewank Rastogi" → "Dewank R"
 *      "John" → "John"
 *      "" → ""
 */
function abbreviateName(fullName: string): string {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || "";
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0];
  return `${firstName} ${lastInitial}`;
}

/**
 * Reviewer name shown on cards and gallery captions. Anonymous reviews show the full
 * "Anonymous Student" label (abbreviating it would render "Anonymous S").
 */
function getReviewerDisplayName(review: RawReview): string {
  // Anonymous: blank, so the card shows the translated propertypage.reviews.anonymoususer label.
  if (isAnonymousReview(review)) return "";
  return abbreviateName(review.user_details?.name || "");
}

/** "Anonymous Student" for a student who asked to be anonymous; "Anonymous" for a review with no name. */
function getReviewerNameKey(anonymous: boolean, hasRealName: boolean): string | undefined {
  if (anonymous) return ANONYMOUS_REVIEWER_KEY;
  return hasRealName ? undefined : NAMELESS_REVIEWER_KEY;
}

function transformReview(
  review: RawReview,
  index: number,
  propertyName?: string,
  prevHeading?: string,
): ReviewData {
  const photos: ReviewPhoto[] = [];
  review?.media?.forEach((item) => {
    if (item?.thumbnail_url || item?.path) {
      photos.push({
        src: item.thumbnail_url || item.path || "",
        alt: item.thumbnail_url ? "Review video" : "Review photo",
        isVideo: Boolean(item.thumbnail_url),
        platform: item.thumbnail_url ? item.platform || REVIEW_VIDEO_PLATFORM : undefined,
      });
    }
  });

  const bd = review.booking_details;
  const ud = review.user_details;

  const rawTitle = typeof review?.heading === "object" ? review.heading?.heading : review?.heading;

  let title: string;
  if (rawTitle) {
    title = rawTitle;
  } else {
    title = getFallbackHeading(review.rating || 3, index, prevHeading);
  }

  const anonymous = isAnonymousReview(review);
  const hasRealName = Boolean(ud?.name) && !anonymous;
  const hasRealUniversity = Boolean(bd?.university || ud?.university);

  return {
    id: review.id || `review-${index}`,
    title,
    rating: review.rating || 0,
    roomType: bd?.room_type || review.room_type || ud?.room_type || "",
    dateRange: formatReviewDateRange(bd?.tenancy || review.booking_dates),
    body: review.content || review.review || "",
    photos,
    helpfulCount: review.helpful_count || 0,
    bookingDate: formatDate(review.created_at || review.timestamp) || review.booking_date || "",
    reviewer: {
      name: getReviewerDisplayName(review),
      nameKey: getReviewerNameKey(anonymous, hasRealName),
      avatar: anonymous
        ? getRandomAvatar("", index)
        : ud?.avatar || getRandomAvatar(ud?.name || "", index),
      university: bd?.university || ud?.university || "",
      universityKey: hasRealUniversity ? undefined : "propertypage.reviews.studentin",
      universityParam: hasRealUniversity ? undefined : propertyName || "",
      program: ud?.program || "",
      nationality: bd?.nationality || "",
      enrolmentStatus: bd?.enrolment_status || "",
    },
  };
}

/** No body and no student-written heading. Our fallback heading doesn't count. */
function isEmptyReview(review: RawReview): boolean {
  const heading = typeof review.heading === "object" ? review.heading?.heading : review.heading;
  return !(review.content || review.review || "").trim() && !(heading || "").trim();
}

function buildMediaArray(rawReviews: RawReview[]) {
  const mediaArray: any[] = [];
  const mediaIndexMap = new Map<string, number>();

  rawReviews.forEach((rev, idx) => {
    mediaIndexMap.set(rev.id || `rev-${idx}`, mediaArray.length);
    const roomTypeName = rev.booking_details?.room_type || rev.room_type || "";
    const roomTypeId = rev.booking_details?.room_type_id || "";
    const displayName = getReviewerDisplayName(rev);

    rev?.media?.forEach((item) => {
      if (item?.thumbnail_url) {
        mediaArray.push({
          rating: rev.rating,
          thumbnail_url: item.thumbnail_url,
          path: item.path,
          caption: displayName,
          index: mediaArray.length,
          platform: item.platform || REVIEW_VIDEO_PLATFORM,
          category: "videos",
          roomTypeName,
          roomTypeId,
          userName: displayName,
          anonymous: isAnonymousReview(rev),
          showCaption: true,
        });
      } else if (item?.path) {
        mediaArray.push({
          rating: rev.rating,
          base_path: item.path,
          path: item.path,
          caption: displayName,
          index: mediaArray.length,
          category: "images",
          roomTypeName,
          roomTypeId,
          userName: displayName,
          anonymous: isAnonymousReview(rev),
          showCaption: true,
        });
      }
    });
  });

  return { mediaArray, mediaIndexMap };
}

function buildPhotoGallery(mediaArray: any[]): PhotoItem[] {
  if (mediaArray.length === 0) return [];
  return mediaArray.slice(0, 7).map((media, idx) => ({
    id: `media-${idx}`,
    src: media.thumbnail_url || media.path || "",
    alt: media.category === "videos" ? "Review video" : "Review photo",
    isVideo: media.category === "videos",
    platform: media.category === "videos" ? media.platform : undefined,
    overlayCount: idx === 6 && mediaArray.length > 7 ? mediaArray.length - 6 : undefined,
    overlayText: undefined,
  }));
}

// eslint-disable-next-line import/prefer-default-export
export function buildReviewsData(input: RawReviewsInput): ReviewsSectionBuiltData {
  const { reviewsRating, reviewSummary, rawReviews, propertyCity, propertyName } = input;
  const hasReviews = rawReviews && rawReviews.length > 0;
  const overallRating = reviewsRating ? parseFloat(String(reviewsRating)) : 0;

  const { mediaArray, mediaIndexMap } = hasReviews
    ? buildMediaArray(rawReviews)
    : { mediaArray: [] as any[], mediaIndexMap: new Map<string, number>() };

  const reviews = hasReviews
    ? rawReviews.reduce<ReviewData[]>((acc, rev, idx) => {
        const prevHeading = acc.length > 0 ? acc[acc.length - 1].title : undefined;
        acc.push(transformReview(rev, idx, propertyCity || propertyName, prevHeading));
        return acc;
      }, [])
    : [];

  return {
    overallRating,
    ratingLabel: getRatingLabel(overallRating),
    reviewCount: hasReviews ? rawReviews.length : 0,
    categories: buildCategories(reviewSummary?.rating),
    summary: reviewSummary?.summary || "",
    photos: buildPhotoGallery(mediaArray),
    reviews,
    carouselReviews: reviews.filter((_, idx) => rawReviews && !isEmptyReview(rawReviews[idx])),
    mediaArray,
    mediaIndexMap,
  };
}
